// Готовность к рассылке (031: contracts/api.md, «Маршруты»; FR-010 – FR-016; ASG-RES-02, ASG-RES-03,
// ASG-ONB-12, ASG-ONB-13). Люди пунктов протокола в любом режиме — записи ответственных
// и контрагентов, люди из таблицы, нерешённые упоминания — с группой по правилу адресата.
// «Готовы N из M»: M — все люди и нерешённые упоминания, N — группа ready. Окно — только числа,
// ФИО и текст ячейки (те, кто видит протокол); адресов нет.
import { z } from '../kernel/zod.ts';
import { DateSchema, IdSchema, TimestampSchema } from '../kernel/common.ts';
import { ProtocolMailingSchema } from '../delivery/mailing.ts';

/** Группы окна по порядку на экране (FR-013, сценарий 1.2). */
export const readinessGroups = [
  'ready',
  'unresolved',
  'unclear',
  'no_email',
  'email_unconfirmed',
  'corporate_only',
  'not_addressee',
  'org_no_mail',
] as const;
export type ReadinessGroup = (typeof readinessGroups)[number];
export const ReadinessGroupSchema = z.enum(readinessGroups);

export const readinessGroupLabels = {
  ready: 'Готовы',
  unresolved: 'Ждёт решения',
  unclear: 'Неясно',
  no_email: 'Нет почты',
  email_unconfirmed: 'Адрес не подтверждён',
  corporate_only: 'Адрес вне корпоративных доменов',
  not_addressee: 'Не адресат',
  org_no_mail: 'Компания без «получает письма»',
} as const satisfies Record<ReadinessGroup, string>;

export const ReadinessPersonSchema = z.object({
  /** record:<id>, mention:<id> или text:<ключ> — строка окна. */
  key: z.string(),
  /** ФИО записи или текст ячейки, как записан. */
  name: z.string(),
  recordId: IdSchema.nullable(),
  mentionId: IdSchema.nullable(),
  /** Открытые пункты протокола у человека. */
  items: z.number().int().min(0),
  /**
   * 032 (FR-019, принцип XV): руководитель не определён — копия руководителю не уйдёт. Пометка,
   * а не группа: «Готовы N из M» она не меняет.
   */
  noManager: z.literal(true).optional(),
  /** 032 (FR-015): виды писем, от которых человек отписался. */
  unsubscribed: z.array(z.enum(['meeting_tomorrow', 'morning'])).optional(),
  /** 032 (FR-023): «В отъезде до» — последний день отъезда. */
  away: DateSchema.optional(),
});
export type ReadinessPerson = z.infer<typeof ReadinessPersonSchema>;

/** День планёрки (FR-023): set — ведущий или контролёр; snapshots, week — по умолчанию; none — нет. */
export const meetingDaySources = ['set', 'snapshots', 'week', 'none'] as const;
export const MeetingDaySchema = z.object({
  /** 1 — понедельник … 7 — воскресенье; null — дня планёрки нет (письмо — понедельник). */
  weekday: z.number().int().min(1).max(7).nullable(),
  source: z.enum(meetingDaySources),
});
export type MeetingDay = z.infer<typeof MeetingDaySchema>;

/** POST /api/protocols/{id}/meeting-day: день недели или null — «по снимкам». */
export const MeetingDayBodySchema = z.strictObject({
  weekday: z.number().int().min(1).max(7).nullable(),
});

/** GET /api/protocols/{id}/readiness. */
export const ProtocolReadinessSchema = z.object({
  ready: z.number().int().min(0),
  total: z.number().int().min(0),
  groups: z.array(
    z.object({ group: ReadinessGroupSchema, people: z.array(ReadinessPersonSchema) }),
  ),
  meetingDay: MeetingDaySchema,
  mailing: ProtocolMailingSchema,
});
export type ProtocolReadiness = z.infer<typeof ProtocolReadinessSchema>;

/** GET /api/protocols/{id}/funnel — «Кто ещё не вошёл» (FR-016; ASG-ONB-13). */
export const ProtocolFunnelSchema = z.object({
  ready: z.number().int().min(0),
  delivered: z.number().int().min(0),
  loggedIn: z.number().int().min(0),
  acted: z.number().int().min(0),
  people: z.array(
    z.object({
      recordId: IdSchema,
      delivered: TimestampSchema.nullable(),
      loggedIn: TimestampSchema.nullable(),
      firstAction: TimestampSchema.nullable(),
    }),
  ),
});
export type ProtocolFunnel = z.infer<typeof ProtocolFunnelSchema>;
